import React, {ReactElement, useEffect, useState} from "react";
import {ModalBody} from "@/widgets/ModalParts";
import {useDispatch, useSelector} from "react-redux";
import {IStore} from "@/redux/defaultStore";
import EditImageModal from "./EditImageModal";
import {addError} from "@/redux/meta/metaActions";
import Toast from "../Toast";
import {useDeleteAvatar} from "@/hooks/useDeleteAvatar";
import {useGetUserLeaderboardNickname} from "@/hooks/useGetUserLeaderboardNickname";
import {USE_GET_USER_PROFILE_KEY, useGetUserProfile} from "@/hooks/useGetUserProfile";
import EditProfile from "@/components/modals/editProfileModal/EditProfile";
import {DragEvents} from "@/components/modals/editProfileModal/dragEvents";
import EditProfilePicture from "@/components/modals/editProfileModal/EditProfilePicture";
import {useParams} from "react-router";
import {Modal} from "@/widgets/Modal";
import {useQueryClient} from "@tanstack/react-query";
import {sendMessageToOW} from "@/hooks/useOverwolfInterop";
import {useUpdateLeaderboardNickname} from "@/hooks/useUpdateLeaderboardNickname";

interface Props {
    isOpen: boolean;
    onClose: () => void;
    onProfilePictureUpdate: () => void;
    hideNftOption?: boolean;
}

enum ImageContentTypes {
    GIF = "image/gif",
    JPG = "image/jpeg",
    PNG = "image/png",
    WEBP = "image/webp",
}

function EditProfilePictureModal(props: Props): ReactElement {
    const {slug: brandMapSlug} = useParams<{ slug: string }>();
    const dispatch = useDispatch();
    const fullToken = useSelector((store: IStore) => store.authStore.fullToken);
    const [
        inputName,
        setInputNameRaw,
    ] = useState<string>("");
    /*
     * Inline nickname error (e.g. "taken") shown under the input. Cleared
     * automatically when the user edits the field via the wrapped setter so
     * the message disappears the moment they start fixing it.
     */
    const [nameError, setNameError] = useState<string | null>(null);
    const setInputName = (val: string) => {
        setInputNameRaw(val); setNameError(null);
    };
    const [
        dragState,
        setDragState,
    ] = useState<DragEvents>(DragEvents.ExitDocument);
    const [
        image,
        setImage,
    ] = useState<string>("");
    const [
        editImageModal,
        setEditImageModal,
    ] = useState<boolean>(false);
    const [
        count,
        setCount,
    ] = useState<number>(0);
    const [
        showToast,
        setShowToast,
    ] = useState<boolean>(false);
    const {data: userLeaderboardNickname} = useGetUserLeaderboardNickname(fullToken);
    const {data: currentUser, refetch: refetchUserProfile} = useGetUserProfile(fullToken);
    const [
        showPfpOptions,
        setShowPfpOptions,
    ] = useState<boolean>(false);
    const {refetch: refetchLeaderboardNickname} = useGetUserLeaderboardNickname(fullToken);
    const queryClient = useQueryClient();
    const {mutateAsync: deleteAvatar} = useDeleteAvatar();
    const {mutateAsync: updateNicknameCall} = useUpdateLeaderboardNickname();

    useEffect(() => {
        if (userLeaderboardNickname) {
            setInputName(userLeaderboardNickname.nickname);
        }
    }, [userLeaderboardNickname]);

    useEffect(() => {
        if (props.isOpen) {
            setShowPfpOptions(false);
        }
    }, [props.isOpen]);

    /*
     * Returns true on success, false when a nickname-specific FormError
     * (taken / not allowed / validation) surfaced inline. The caller uses
     * the return value to decide whether to close the modal — closing on a
     * silent inline error would discard the error message before the user
     * could read it.
     */
    async function updateNickname(): Promise<boolean> {
        try {
            await updateNicknameCall(inputName);
            queryClient.invalidateQueries({queryKey: [USE_GET_USER_PROFILE_KEY]});
            const {data: nickname} = await refetchLeaderboardNickname();
            /*
             * Tell Overwolf to update the user data. Optional-chain because
             * a degraded refetch can return undefined; the Overwolf bridge
             * accepts undefined payload fields without crashing.
             */
            sendMessageToOW({
                type: "de:update-user",
                payload: {
                    nickname: nickname?.nickname,
                },
            });
            setShowToast(true);
            return true;
        } catch (e) {
            /*
             * @devour/client errors are raw Response objects — body via
             * `.json()`. Clone first so a fallthrough to addError can still
             * parse the body. Backend wraps formErrors values as ApiError
             * instances ({type, message}); accept bare-string shape too.
             */
            let body: any = null;
            try {
                body = await (e as any)?.clone?.()?.json();
            } catch { /* not a Response, body consumed, or invalid JSON */ }
            const raw = body?.formErrors?.nickname;
            const code = typeof raw === "string" ? raw : raw?.message;
            if (code === "Taken") {
                setNameError("That nickname is taken. Please pick another.");
                return false;
            }
            if (code === "NotAllowed") {
                setNameError("That nickname is not allowed, choose another.");
                return false;
            }
            dispatch(await addError(e));
            return false;
        }
    }
    function handleDragEvents(event: DragEvents): React.DragEventHandler<HTMLDivElement> {
        return (e) => {
            e.preventDefault();
            e.stopPropagation();

            if (event === dragState) {
                return;
            }

            switch (event) {
                case DragEvents.Drop:
                    if (dragState === DragEvents.Hover) {
                        uploadPhoto(e);
                    }
                    setDragState(DragEvents.ExitDocument);
                    return;
                case DragEvents.Hover:
                    e.dataTransfer.dropEffect = "copy";
                    setDragState(event);
                    return;
                case DragEvents.Enter:
                case DragEvents.ExitDocument:
                case DragEvents.ExitHover:
                default:
                    setDragState(event);

            }
        };
    }

    function uploadPhoto(e): void {
        let files;
        if (e.dataTransfer) {
            files = e.dataTransfer.files;
        } else if (e.target) {
            files = e.target.files;
        }

        const reader = new FileReader();
        reader.onload = () => {
            setImage(reader.result as string);
        };

        if (files && files.length > 0 && files[0] && Object.values(ImageContentTypes).includes(files[0].type)) {
            reader.readAsDataURL(files[0]);
            setEditImageModal(true);
        } else if (!files || files.length === 0) {
            // User cancelled file picker - silently ignore

        } else {
            addError(new Error("The uploaded file is not a supported content type. Please try again.") as any).then(res => {
                dispatch(res);
            })
                .catch(e => {
                    dispatch(e);
                });
        }
    }

    async function editImageModalOnClose(): Promise<void> {
        queryClient.invalidateQueries({queryKey: [USE_GET_USER_PROFILE_KEY]});
        setCount(count + 1);
        props.onProfilePictureUpdate();
        setShowPfpOptions(false);
        setEditImageModal(false);
    }

    async function removeAvatar(): Promise<void> {
        try {
            await deleteAvatar(currentUser?.user.profilePicture?.id);
            await refetchUserProfile();
            setCount(count + 1);
            props.onProfilePictureUpdate();
            setShowToast(true);
        } catch (e) {
            dispatch(await addError(e));
        }
    }

    function handleToastDismissal() {
        setShowToast(false);
    }

    return (
        <>
            <Toast
                message="Your changes were saved!"
                isOpen={showToast}
                onDismiss={handleToastDismissal}
                removeMarginAdjustment={!!brandMapSlug}
            />
            <div
                onDragOver={dragState !== DragEvents.Hover
                    ? handleDragEvents(DragEvents.Enter)
                    : undefined}
                onDragEnd={handleDragEvents(DragEvents.ExitDocument)}
                onDragLeave={handleDragEvents(DragEvents.ExitDocument)}
                onDrop={handleDragEvents(DragEvents.Drop)}
            >
                <EditImageModal
                    image={image}
                    isOpen={editImageModal}
                    onClose={editImageModalOnClose}
                />
                <Modal
                    isOpen={props.isOpen}
                    toggle={props.onClose}
                    contentClassName="edit-profile-modal"
                >
                    <ModalBody
                        className="edit-profile-modal_body"
                    >
                        {!showPfpOptions
                            ? <EditProfile
                                onClose={props.onClose}
                                handleDragEvents={handleDragEvents}
                                count={count}
                                dragState={dragState}
                                inputName={inputName}
                                setInputName={setInputName}
                                removeAvatar={removeAvatar}
                                setShowPfpOptions={setShowPfpOptions}
                                nameError={nameError}
                            />
                            : <EditProfilePicture
                                onClose={props.onClose}
                                handleDragEvents={handleDragEvents}
                                count={count}
                                dragState={dragState}
                                setInputName={setInputNameRaw}
                                removeAvatar={removeAvatar}
                                setShowPfpOptions={setShowPfpOptions}
                                uploadPhoto={uploadPhoto}
                                hideNftOption={props.hideNftOption}
                            />
                        }
                        {!showPfpOptions &&
                            <footer className="edit-profile-modal_footer">
                                <button
                                    type="button"
                                    className="edit-profile-modal_footer_button edit-profile-modal_footer_button--secondary"
                                    onClick={props.onClose}
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    className="edit-profile-modal_footer_button edit-profile-modal_footer_button--primary"
                                    onClick={async () => {
                                        const ok = await updateNickname();
                                        if (ok) props.onClose();
                                    }}
                                >
                                    Save Changes
                                </button>
                            </footer>
                        }
                    </ModalBody>
                </Modal>
            </div>
        </>
    );
}

export default EditProfilePictureModal;
